/**
 * State synchronization — NS solver state ↔ ReactFlow node data.
 *
 * After each NS step, writes computed T, P, mdot, Q back to the
 * ReactFlow nodes for display in the property panel.
 *
 * Handles all component types including cooling network components:
 * pump, heatExchanger, coolingTower, dataCenter, tank, reactor, turbine, compressor.
 */

import type { Edge, Node } from 'reactflow';
import type { NSSolverState, PipeSegment } from '../types';
import { propsFromPH_SI } from '../core/fluidAdapter';

/**
 * Derive specific entropy + enthalpy (display units) from a representative
 * (P, h) state, for the T-s / p-h result-diagram adapters.
 *
 * The NS network stores junction/cell state as (P [Pa], h [J/kg]). The IAPWS-IF97
 * fluid adapter returns s in J/(kg·K) and h in J/kg; we convert both to the
 * kJ-based display convention (CLAUDE.md: kJ/kg, kJ/(kg·K)) at this boundary.
 *
 * READ-ONLY: derived from already-solved state. Returns null on any non-finite
 * input or property-lookup failure so callers leave the fields untouched (the
 * diagram panels then show their honest empty-state rather than NaN points).
 *
 * @param P_Pa  Pressure in Pa
 * @param h_Jkg Specific enthalpy in J/kg
 * @returns { entropy_kJkgK, enthalpy_kJkg } or null when unavailable.
 */
function deriveEntropyEnthalpy(
  P_Pa: number,
  h_Jkg: number,
): { entropy_kJkgK: number; enthalpy_kJkg: number } | null {
  if (!Number.isFinite(P_Pa) || P_Pa <= 0 || !Number.isFinite(h_Jkg)) return null;
  try {
    const props = propsFromPH_SI(P_Pa, h_Jkg);
    const s_kJkgK = props.s / 1000; // J/(kg·K) → kJ/(kg·K)
    const h_kJkg = props.h / 1000;  // J/kg → kJ/kg
    if (!Number.isFinite(s_kJkgK) || !Number.isFinite(h_kJkg)) return null;
    return { entropy_kJkgK: s_kJkgK, enthalpy_kJkg: h_kJkg };
  } catch {
    return null;
  }
}

/**
 * Sync NS solver results back to ReactFlow node data.
 * Updates temperature, pressure, mass flow, quality fields.
 */
export function syncNSToNodes(nodes: Node[], state: NSSolverState): Node[] {
  return nodes.map(node => {
    const data = { ...node.data };

    // Find the outlet junction for this component
    const outJunction = Array.from(state.junctions.values())
      .find(j => j.componentId === node.id);

    // Find the NS component (for reading internal state like pump speed)
    const nsComp = state.components.get(node.id);

    // Representative SOLVED mass flow for this node (largest |ṁ| across its
    // junction's connected faces). Needed BEFORE the switch so the power
    // mirroring (D-F2) can convert specific work (J/kg) to power (W = ṁ·w).
    let nodeMdot = 0;       // kg/s, magnitude
    let nodeMdotSigned = 0; // kg/s, sign preserved for flow direction
    for (const junction of state.junctions.values()) {
      if (junction.componentId !== node.id) continue;
      for (const conn of junction.connections) {
        const pipe = state.pipes.get(conn.pipeId);
        if (!pipe) continue;
        const face = pipe.faces[conn.faceIndex];
        const absMdot = Math.abs(face.mdot);
        if (absMdot > nodeMdot) {
          nodeMdot = absMdot;
          nodeMdotSigned = face.mdot;
        }
      }
    }

    if (outJunction) {
      // Temperature from the solved (P, h) state via IAPWS (D-F1 — no clamp)
      const T_C = outJunction.h > 0
        ? displayTempC(outJunction.P, outJunction.h)
        : data.currentTemp;
      // Pressure in kPa for display
      const P_kPa = outJunction.P / 1000;

      // Get inlet junction temperature (find junction where this component's pipe is inflow)
      const inJunction = findInletJunction(state, node.id);
      const T_in_C = inJunction && inJunction.h > 0
        ? displayTempC(inJunction.P, inJunction.h)
        : T_C;

      switch (node.type) {
        case 'reactor':
          data.currentTemp = T_C;
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          break;

        case 'turbine':
        case 'compressor':
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          data.inletTemp = T_in_C;
          // D-F2: mirror the SOLVED work back onto the node — the KPI panel
          // reads data.powerOutput / data.powerInput, which previously kept
          // their template zeros while the solver's work sat unread in
          // _lastMetadata. W_turbine / W_compressor are SPECIFIC work (J/kg,
          // = |Δh| of the expansion/compression); power = ṁ·w, displayed in
          // MW — exactly the pump branch's W→MW convention (:powerConsumption).
          if (node.type === 'turbine' && nsComp) {
            const turbMeta = nsComp.params._lastMetadata;
            if (turbMeta) {
              if (typeof turbMeta.W_turbine === 'number' && Number.isFinite(turbMeta.W_turbine)) {
                data.powerOutput = (turbMeta.W_turbine * nodeMdot) / 1e6; // J/kg × kg/s → W → MW
              }
              // R-Surface: turbine exit-wetness diagnostics (turbine-wetness
              // cluster). Read-only _lastMetadata mirrors; sentinel conventions
              // per fluidAdapter (0..1 two-phase, -1 subcooled, >1 superheated).
              if (typeof turbMeta.exitQuality === 'number' && Number.isFinite(turbMeta.exitQuality)) {
                data.exitQuality = turbMeta.exitQuality;
              }
              if (typeof turbMeta.wetnessFraction === 'number' && Number.isFinite(turbMeta.wetnessFraction)) {
                data.wetnessFraction = turbMeta.wetnessFraction;
              }
              data.wetnessExceedsLimit = turbMeta.wetnessExceedsLimit === 1;
              data.wetnessUnavailable = turbMeta.wetnessUnavailable === 1;
            }
          }
          if (node.type === 'compressor' && nsComp) {
            const compMeta = nsComp.params._lastMetadata;
            if (compMeta && typeof compMeta.W_compressor === 'number' && Number.isFinite(compMeta.W_compressor)) {
              data.powerInput = (compMeta.W_compressor * nodeMdot) / 1e6; // J/kg × kg/s → W → MW
            }
          }
          break;

        case 'pump':
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          data.inletTemp = T_in_C;
          data.inletPressure = inJunction ? inJunction.P / 1000 : data.inletPressure;
          data.pressureRise = inJunction ? (outJunction.P - inJunction.P) / 1000 : data.pressureRise;
          data.head = inJunction ? (outJunction.P - inJunction.P) / (998 * 9.81) : data.head;
          // Read back actual pump speed and power from NS component
          if (nsComp) {
            data.actualSpeed = nsComp.params._actualSpeed ?? nsComp.params.speed;
            data.powerConsumption = (nsComp.params._lastMetadata?.W_pump ?? 0) / 1e6; // W → MW
            // R-Surface: NPSH diagnostics (pump-npsh cluster). Read-only outputs
            // already computed by NSPump.applyBoundaryConditions and mirrored on
            // _lastMetadata. npshAvailable is metres; cavitationRisk is a 0/1 flag
            // (metadata is Record<string, number>) re-exposed here as a boolean.
            const pumpMeta = nsComp.params._lastMetadata;
            if (pumpMeta) {
              const npshA = pumpMeta.npshAvailable_m;
              if (typeof npshA === 'number' && Number.isFinite(npshA)) {
                data.npshAvailable_m = npshA; // m
              }
              data.cavitationRisk = pumpMeta.cavitationRisk === 1;
              // NPSH-required needs a pump datasheet we do not have → flagged NaN.
              data.npshRequired_m = pumpMeta.npshRequired_m; // NaN by design
              const pVap = pumpMeta.npshVaporPressure_Pa;
              if (typeof pVap === 'number' && Number.isFinite(pVap)) {
                data.npshVaporPressure_kPa = pVap / 1000; // Pa → kPa for display
              }
            }
          }
          break;

        case 'heatExchanger':
          // HX: outlet temp is the exit temperature of the fluid passing through this side
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          data.inletTemp = T_in_C;
          // Determine which side this is (hot or cold) from temperature delta
          if (T_C > T_in_C) {
            // Fluid is being heated → this is the cold side
            data.coldInTemp = T_in_C;
            data.coldOutTemp = T_C;
          } else {
            // Fluid is being cooled → this is the hot side
            data.hotInTemp = T_in_C;
            data.hotOutTemp = T_C;
          }
          // N1 display truth: report the duty the solve APPLIED to the fluid
          // (params._appliedQ_W, written by nsNetwork at apply time from the
          // post-clamp outlet enthalpies), NOT the configured Q_rate input.
          // 0 when the network carried no throughflow — honestly "no duty".
          if (nsComp) {
            const Q = nsComp.params._appliedQ_W ?? 0;
            data.heatRejected = Math.abs(Q) / 1e6; // W → MW
            data.coolingCapacity = Math.abs(Q) / 1e6;
            // R-Surface: effectiveness-NTU diagnostics (hx-eps-ntu cluster).
            // Read-only outputs from NSHeatExchanger.applyBoundaryConditions,
            // mirrored on _lastMetadata. Emitted only when the solver supplied the
            // stream-capacity context (else the HX omits them — we do not invent).
            // eps/Cr are dimensionless in [0,1]; NTU ≥ 0; Q is W → display in MW.
            const hxMeta = nsComp.params._lastMetadata;
            if (hxMeta) {
              if (typeof hxMeta.hx_effectiveness === 'number' && Number.isFinite(hxMeta.hx_effectiveness)) {
                data.hx_effectiveness = hxMeta.hx_effectiveness;
              }
              if (typeof hxMeta.hx_NTU === 'number' && Number.isFinite(hxMeta.hx_NTU)) {
                data.hx_NTU = hxMeta.hx_NTU;
              }
              if (typeof hxMeta.hx_Cr === 'number' && Number.isFinite(hxMeta.hx_Cr)) {
                data.hx_Cr = hxMeta.hx_Cr;
              }
              if (typeof hxMeta.hx_Q === 'number' && Number.isFinite(hxMeta.hx_Q)) {
                data.hx_Q = hxMeta.hx_Q / 1e6; // W → MW for display
              }
            }
          }
          break;

        case 'coolingTower': {
          // Cooling tower modeled as condenser or HX — show outlet water temp
          data.waterOutTemp = T_C;
          data.waterInTemp = T_in_C;
          data.currentTemp = T_C;
          data.outletTemp = T_C;
          if (nsComp) {
            // N1 display truth. Condenser-mapped towers: applied rejection =
            // solved per-kg Q_reject (J/kg, _lastMetadata) × solved ṁ (kg/s).
            // HX-mapped towers: the network-recorded _appliedQ_W.
            const qRejectPerKg = nsComp.params._lastMetadata?.Q_reject;
            const appliedW =
              typeof qRejectPerKg === 'number' && Number.isFinite(qRejectPerKg)
                ? qRejectPerKg * nodeMdot
                : (nsComp.params._appliedQ_W ?? 0);
            data.heatRejected = Math.abs(appliedW) / 1e6; // W → MW
            data.coolingCapacity = Math.abs(appliedW) / 1e6;
          }
          break;
        }

        case 'dataCenter':
          // Data center modeled as heat source — show coolant outlet temp
          data.currentTemp = T_C;
          data.outletTemp = T_C;
          data.inletTemp = T_in_C;
          if (nsComp) {
            // N1 display truth: applied duty, not the configured computeLoad.
            data.heatInput = Math.abs(nsComp.params._appliedQ_W ?? 0) / 1e6;
          }
          break;

        case 'tank':
        case 'accumulator':
        case 'pressurizer':
          data.currentTemp = T_C;
          data.pressure = P_kPa;
          break;

        case 'expansionValve':
          data.outletTemp = T_C;
          data.inletTemp = T_in_C;
          data.outletPressure = P_kPa;
          break;

        case 'ejector':
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          if (nsComp) {
            data.entrainmentRatio = nsComp.params._entrainmentRatio ?? data.entrainmentRatio;
          }
          break;

        case 'absorber':
          data.operatingTemp = T_C;
          data.outletTemp = T_C;
          // N1 display truth: applied duty from the solve, not coolingCapacity.
          if (nsComp) data.heatRejected = Math.abs(nsComp.params._appliedQ_W ?? 0) / 1e6;
          break;

        case 'generator':
          data.operatingTemp = T_C;
          data.outletTemp = T_C;
          // N1 display truth: applied duty from the solve, not heatInput param.
          if (nsComp) data.heatInput = Math.abs(nsComp.params._appliedQ_W ?? 0) / 1e6;
          break;

        case 'thermalStorage':
          data.currentTemp = T_C;
          data.pressure = P_kPa;
          break;

        case 'mixer':
        case 'splitter':
        case 'junction':
          data.outletTemp = T_C;
          data.pressure = P_kPa;
          data.outletPressure = P_kPa;
          break;

        case 'boundaryCondition':
          // BCs are fixed — don't overwrite user-set values, just show flow
          break;

        default:
          data.currentTemp = T_C;
          data.outletTemp = T_C;
          data.outletPressure = P_kPa;
          break;
      }

      // R-Surface: per-node ENTROPY (kJ/kg·K) + ENTHALPY (kJ/kg) for the T-s /
      // p-h result diagrams. The diagram adapters (diagramAdapters.ts) read the
      // `entropy` and `enthalpy` data fields (with temperature/pressure already
      // synced above), so writing them here is what lets those panels leave their
      // empty-state for a solved network. Derived from the outlet junction's
      // representative (P, h) via the IAPWS-IF97 adapter; left untouched when the
      // state is non-finite or the lookup fails (panel keeps its honest empty
      // state — never a NaN point). BCs are skipped above (no overwrite), but
      // their junction still carries a valid (P, h) so we still emit s/h for them
      // to anchor the diagram. Does NOT touch the solver.
      const sh = deriveEntropyEnthalpy(outJunction.P, outJunction.h);
      if (sh) {
        data.entropy = sh.entropy_kJkgK;   // kJ/(kg·K)
        data.enthalpy = sh.enthalpy_kJkg;  // kJ/kg
      }
    }

    // --- Pipe nodes: read ΔP, flow, temperatures directly from the pipe segment ---
    if (node.type === 'pipe') {
      const pipe = state.pipes.get(node.id);
      if (pipe && pipe.nCells > 0) {
        // Find boundary junction pressures connected to this pipe (more reliable than cell values)
        let bcInletP: number | null = null;
        let bcOutletP: number | null = null;
        for (const junction of state.junctions.values()) {
          if (junction.type !== 'boundary') continue;
          for (const conn of junction.connections) {
            if (conn.pipeId !== node.id) continue;
            if (conn.faceIndex === 0) bcInletP = junction.P;
            if (conn.faceIndex === pipe.nCells) bcOutletP = junction.P;
          }
        }

        // Use BC pressures when available (always correct), fall back to cell pressures
        const P_in = bcInletP ?? pipe.cells[0].P;
        const P_out = bcOutletP ?? pipe.cells[pipe.nCells - 1].P;
        data.pressureDrop = (P_in - P_out) / 1000; // Pa → kPa
        data.inletPressure = P_in / 1000;
        data.outletPressure = P_out / 1000;
        data.inletTemp = pipe.cells[0].T - 273.15;
        data.outletTemp = pipe.cells[pipe.nCells - 1].T - 273.15;
        // Average mass flow from faces
        const avgMdot = pipe.faces.reduce((s, f) => s + Math.abs(f.mdot), 0) / (pipe.nCells + 1);
        data.massFlow = avgMdot;
        data.currentFlowRate = avgMdot;
        // Average velocity from mdot / (rho * A)
        const rho_avg = pipe.cells.reduce((s, c) => s + c.rho, 0) / pipe.nCells;
        data.velocity = rho_avg > 0 ? avgMdot / (rho_avg * pipe.area) : 0;

        // --- Heat loss (R Sim 1-a: pipe-heatloss-sync) ---
        // The NS core applies wall heat to the fluid per cell (energySolver.ts:
        // wallHeatFlux·A_wall + wall thermal-inertia term, positive = heat INTO fluid)
        // but never accumulates a per-pipe total. Derive the net heat exchange from
        // the steady-state enthalpy balance across the pipe: Q = ṁ·(h_in − h_out).
        //
        // Sign convention for the node's `heatLoss` field (PipeData.heatLoss, MW):
        // POSITIVE = heat LEAVING the fluid (a loss), matching the UI definition
        // Q_loss = U·A·(T_fluid − T_amb). When the fluid cools along the pipe,
        // h_out < h_in, so ṁ·(h_in − h_out) > 0.
        //
        // Use the signed average face mass flow so the sign is correct for both
        // forward (mdot > 0) and reverse (mdot < 0) flow. Pairing the signed mdot
        // with the fixed cell ordering (h[0] − h[last]) yields a positive loss in
        // either direction: for reversed flow the physical inlet is the last cell,
        // and the two sign flips cancel.
        const signedMdot = pipe.faces.reduce((s, f) => s + f.mdot, 0) / (pipe.nCells + 1);
        const h_first = pipe.cells[0].h;              // J/kg (cell[0])
        const h_last = pipe.cells[pipe.nCells - 1].h; // J/kg (cell[nCells-1])
        // Q_loss [W] = ṁ_signed [kg/s] · (h_first − h_last) [J/kg]
        const Q_loss_W = signedMdot * (h_first - h_last);
        data.heatLoss = Q_loss_W / 1e6; // W → MW (PipeData.heatLoss is MW)

        // R-Surface: per-node ENTROPY (kJ/kg·K) + ENTHALPY (kJ/kg) for the T-s /
        // p-h diagrams, derived from the pipe's representative (outlet cell)
        // state. Read-only; left untouched if the lookup fails.
        const outCell = pipe.cells[pipe.nCells - 1];
        const shPipe = deriveEntropyEnthalpy(outCell.P, outCell.h);
        if (shPipe) {
          data.entropy = shPipe.entropy_kJkgK;   // kJ/(kg·K)
          data.enthalpy = shPipe.enthalpy_kJkg;  // kJ/kg
        }
      }
    }

    // Connected-face mass flow display fields (from the representative solved
    // flow computed BEFORE the switch — same max-|ṁ| selection as before).
    if (nodeMdot > 0) {
      data.massFlow = nodeMdotSigned; // Preserve sign for flow direction
      data.currentFlowRate = nodeMdot;
      data.outletMassFlow = nodeMdot;
    }

    // NS solver convergence info
    data._nsConverged = state.converged;
    data._nsResidual = state.residuals.mass;

    return { ...node, data };
  });
}

/**
 * Find the inlet junction for a component by tracing back through the pipe network.
 * The inlet junction is the one where the component's inlet pipe originates.
 */
function findInletJunction(state: NSSolverState, componentId: string): { P: number; h: number } | null {
  // Find the component's junction
  const compJunction = Array.from(state.junctions.values())
    .find(j => j.componentId === componentId);
  if (!compJunction) return null;

  // Find the inflow connection — the pipe bringing flow INTO this junction
  for (const conn of compJunction.connections) {
    if (conn.direction !== 'inflow') continue;
    const pipe = state.pipes.get(conn.pipeId);
    if (!pipe) continue;

    // The inlet conditions are at the upstream end of the inflow pipe
    const upstreamCell = conn.faceIndex === pipe.nCells
      ? pipe.cells[pipe.nCells - 1]  // Flow enters at outlet face → upstream is last cell
      : pipe.cells[0];                // Flow enters at inlet face → upstream is first cell

    return { P: upstreamCell.P, h: upstreamCell.h };
  }

  return null;
}

/**
 * D-F5: sync per-edge flow state onto the canvas edges after each NS step.
 *
 * Writes { phase, massFlow, running } into `edge.data` from the adjacent
 * solved pipe segment — exactly the fields the FlowEdge renderer
 * (src/components/editor/FlowEdge.tsx, FlowEdgeData) consumes for colour /
 * width / motion. Pure sync layer: reads solved state only, never mutates the
 * solver, and returns NEW edge objects (React-safe; inputs untouched).
 *
 * Edge → pipe resolution mirrors networkBuilder's two topologies:
 *   • edge touches a canvas PIPE node → that node id IS the pipe id; the
 *     representative cell is the end nearest this edge (outlet cell when the
 *     pipe is the edge's source, inlet cell when it is the target);
 *   • plain component→component edge → the builder's zero-length connector
 *     `pipe_${source}_${target}`; representative = downstream (last) cell.
 *
 * massFlow is the pipe's mean |face ṁ| (kg/s — drives stroke width; the
 * renderer treats it as a magnitude). phase is the representative cell's
 * Phase enum string ('subcooled' | 'two-phase' | 'superheated' |
 * 'supercritical' — FlowEdge maps them onto its colour buckets). Edges with
 * no matching pipe are returned unchanged (renderer degrades gracefully).
 */
export function syncNSToEdges(edges: Edge[], state: NSSolverState, running: boolean): Edge[] {
  return edges.map(edge => {
    let pipe: PipeSegment | undefined;
    let repCellIdx = -1; // representative cell for phase colour

    const srcPipe = state.pipes.get(edge.source);
    const tgtPipe = state.pipes.get(edge.target);
    if (srcPipe) {
      pipe = srcPipe;                    // pipe → component edge: outlet end
      repCellIdx = srcPipe.nCells - 1;
    } else if (tgtPipe) {
      pipe = tgtPipe;                    // component → pipe edge: inlet end
      repCellIdx = 0;
    } else {
      const connector = state.pipes.get(`pipe_${edge.source}_${edge.target}`);
      if (connector) {
        pipe = connector;                // zero-length connector: downstream end
        repCellIdx = connector.nCells - 1;
      }
    }

    if (!pipe || pipe.nCells <= 0 || repCellIdx < 0) return edge;

    const massFlow =
      pipe.faces.reduce((s, f) => s + Math.abs(f.mdot), 0) / (pipe.nCells + 1);
    const phase = pipe.cells[repCellIdx].phase;

    return {
      ...edge,
      data: { ...(edge.data ?? {}), phase, massFlow, running },
    };
  });
}

/**
 * Display temperature (°C) from the solved junction state (P, h).
 *
 * D-F1 FIX: displayed temperatures now come from the SAME IAPWS-IF97 lookup
 * the solver uses (propsFromPH_SI), replacing the old `h/4182` liquid-cp
 * estimate clamped to [−50, 400] °C — which showed superheated steam at
 * ~717 °C as "400 °C" and mis-mapped every vapour/two-phase state (steam
 * enthalpy is dominated by latent heat, not cp·T).
 *
 * Fallback: if the property lookup fails (state outside the IAPWS-97 range),
 * fall back to the UNCLAMPED liquid estimate h/4182 — approximately right for
 * liquid water, and honestly wrong-looking (rather than silently saturated at
 * a fake 400 °C ceiling) elsewhere.
 */
function displayTempC(P_Pa: number, h_Jkg: number): number {
  if (Number.isFinite(P_Pa) && P_Pa > 0 && Number.isFinite(h_Jkg)) {
    try {
      const T_C = propsFromPH_SI(P_Pa, h_Jkg).T - 273.15;
      if (Number.isFinite(T_C)) return T_C;
    } catch { /* fall through to the liquid-cp estimate */ }
  }
  return h_Jkg / 4182; // liquid-water cp approximation, deliberately unclamped
}
